import { Suspense } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { Gift, LayoutDashboard, Package, Ticket, User, Users, Wallet, type LucideIcon } from 'lucide-react';
import { useAuthStore } from '@/app/stores';
import { Container } from '@/components/ui/Layout';
import { PageLoader } from '@/components/ui/States';
import { cn } from '@/lib/cn';
import { initials } from '@/lib/format';
import { accountNav } from './nav';
import { useVisibleNav } from './Header';

const icons: Record<string, LucideIcon> = { layout: LayoutDashboard, package: Package, wallet: Wallet, gift: Gift, ticket: Ticket, users: Users, user: User };

export function AccountLayout() {
  const user = useAuthStore((s) => s.user)!;
  const links = useVisibleNav(accountNav) as typeof accountNav;
  return (
    <Container className="py-6 sm:py-10">
      <div className="grid gap-6 lg:grid-cols-[16rem_1fr]">
        <aside className="lg:sticky lg:top-24 lg:self-start">
          <div className="mb-4 hidden items-center gap-3 rounded-2xl border border-border bg-card p-4 shadow-card lg:flex">
            <span className="grid size-12 place-items-center rounded-full bg-primary font-bold text-primary-foreground">{initials(user.name)}</span>
            <div className="min-w-0">
              <p className="truncate font-bold">{user.name}</p>
              <p className="truncate text-xs text-muted-foreground">{user.email}</p>
            </div>
          </div>
          <nav aria-label="Account" className="no-scrollbar -mx-4 flex gap-1 overflow-x-auto px-4 lg:mx-0 lg:flex-col lg:rounded-2xl lg:border lg:border-border lg:bg-card lg:p-2 lg:shadow-card">
            {links.map((l) => {
              const Icon = icons[l.icon];
              return (
                <NavLink
                  key={l.to} to={l.to} end={l.to === '/account'}
                  className={({ isActive }) => cn(
                    'flex items-center gap-2.5 whitespace-nowrap rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors',
                    isActive ? 'bg-primary text-primary-foreground' : 'bg-card text-muted-foreground hover:bg-muted hover:text-foreground lg:bg-transparent',
                  )}
                >
                  <Icon className="size-4" />{l.label}
                </NavLink>
              );
            })}
          </nav>
        </aside>
        <div className="min-w-0">
          <Suspense fallback={<PageLoader />}><Outlet /></Suspense>
        </div>
      </div>
    </Container>
  );
}
